import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import { useUscgMapContext } from '../UscgMapContext';
import { getExternalLayerTimeConfig } from '../arcgis-integration/tennesseeNationalGuardLayers';
import {
  applyExternalLayerTimeWindow,
  collectFeatureLayers,
  discoverLayerTimeBounds,
  type LayerTimeApplyMode,
} from './useExternalLayerTimeExtent';

const POLL_MS = 200;
const MAX_POLLS = 20;
export const SHARED_TIME_SLIDER_MAX = 1000;

export type SharedTnGuardLayerTarget = {
  layerId: string;
  preferredDateField: string | null;
  mode: LayerTimeApplyMode;
  dateField: string | null;
};

export type SharedTnGuardLayerTimeRange = {
  start: Date | null;
  end: Date | null;
  setStart: (date: Date) => void;
  setEnd: (date: Date) => void;
  setRangeFromSlider: (startValue: number, endValue: number) => void;
  fullStart: Date | undefined;
  fullEnd: Date | undefined;
  status: 'hidden' | 'loading' | 'ready' | 'unavailable';
  message?: string;
  targetCount: number;
};

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function clampDate(date: Date, min: Date, max: Date): Date {
  const t = date.getTime();
  if (t < min.getTime()) return new Date(min);
  if (t > max.getTime()) return new Date(max);
  return date;
}

export function dateToSliderValue(
  date: Date,
  fullStart: Date,
  fullEnd: Date
): number {
  const span = fullEnd.getTime() - fullStart.getTime();
  if (span <= 0) return 0;
  return Math.round(
    ((date.getTime() - fullStart.getTime()) / span) * SHARED_TIME_SLIDER_MAX
  );
}

export function sliderValueToDate(
  value: number,
  fullStart: Date,
  fullEnd: Date
): Date {
  const span = fullEnd.getTime() - fullStart.getTime();
  const clamped = Math.max(0, Math.min(SHARED_TIME_SLIDER_MAX, value));
  return new Date(
    fullStart.getTime() + (clamped / SHARED_TIME_SLIDER_MAX) * span
  );
}

/**
 * Shared start/stop time range for Ting Outages + FloodingBins Demo. Discovers
 * the union of each layer's full extent and fans out one window.
 */
export function useSharedTnGuardLayerTimeRange(
  candidateLayers: ExternalLayer[]
): SharedTnGuardLayerTimeRange {
  const {
    getAddedExternalLayers,
    isExternalLayersReady,
    externalLayerTimeFilterById,
    setExternalLayerTimeFilter,
    setLayerDefinitionExpressionTimePart,
  } = useUscgMapContext();

  const targetsMeta = useMemo(() => {
    return candidateLayers
      .map((layer) => {
        const config = getExternalLayerTimeConfig(layer);
        if (!config) return null;
        return {
          layerId: String(layer.id),
          preferredDateField: config.preferredDateField ?? null,
        };
      })
      .filter((t): t is NonNullable<typeof t> => t != null);
  }, [candidateLayers]);

  const targetKey = targetsMeta.map((t) => t.layerId).join(',');

  const [targets, setTargets] = useState<SharedTnGuardLayerTarget[]>([]);
  const [fullStart, setFullStart] = useState<Date | undefined>();
  const [fullEnd, setFullEnd] = useState<Date | undefined>();
  const [start, setStartState] = useState<Date | null>(null);
  const [end, setEndState] = useState<Date | null>(null);
  const [status, setStatus] = useState<
    'hidden' | 'loading' | 'ready' | 'unavailable'
  >('hidden');
  const [message, setMessage] = useState<string | undefined>();

  const restoreRef = useRef<{ startIso?: string; endIso?: string }>({});
  // Prefer an existing filter from any target for restore across remounts.
  for (const meta of targetsMeta) {
    const filter = externalLayerTimeFilterById[meta.layerId];
    if (filter?.startIso && filter?.endIso) {
      restoreRef.current = {
        startIso: filter.startIso,
        endIso: filter.endIso,
      };
      break;
    }
  }

  useEffect(() => {
    let cancelled = false;

    const waitForFeatureLayers = async (
      layerId: string
    ): Promise<FeatureLayer[]> => {
      for (let i = 0; i < MAX_POLLS; i++) {
        if (cancelled) return [];
        const layers = getAddedExternalLayers(layerId);
        const featureLayers = collectFeatureLayers(layers);
        if (featureLayers.length > 0) return featureLayers;
        await sleep(POLL_MS);
      }
      return collectFeatureLayers(getAddedExternalLayers(layerId));
    };

    const discover = async () => {
      if (targetsMeta.length === 0) {
        setTargets([]);
        setFullStart(undefined);
        setFullEnd(undefined);
        setStartState(null);
        setEndState(null);
        setStatus('hidden');
        setMessage(undefined);
        return;
      }

      setStatus('loading');
      setMessage(undefined);

      if (!isExternalLayersReady) {
        return;
      }

      const nextTargets: SharedTnGuardLayerTarget[] = [];
      let unionStart: Date | null = null;
      let unionEnd: Date | null = null;

      for (const meta of targetsMeta) {
        const featureLayers = await waitForFeatureLayers(meta.layerId);
        if (cancelled) return;
        const bounds = await discoverLayerTimeBounds(
          featureLayers,
          meta.preferredDateField
        );
        if (cancelled) return;
        if (!bounds.ok) continue;

        nextTargets.push({
          layerId: meta.layerId,
          preferredDateField: meta.preferredDateField,
          mode: bounds.mode,
          dateField: bounds.dateField,
        });
        if (!unionStart || bounds.fullStart.getTime() < unionStart.getTime()) {
          unionStart = bounds.fullStart;
        }
        if (!unionEnd || bounds.fullEnd.getTime() > unionEnd.getTime()) {
          unionEnd = bounds.fullEnd;
        }
      }

      if (cancelled) return;

      if (!unionStart || !unionEnd || nextTargets.length === 0) {
        setTargets([]);
        setFullStart(undefined);
        setFullEnd(undefined);
        setStartState(null);
        setEndState(null);
        setStatus('unavailable');
        setMessage('Time info unavailable for Tennessee Guard layers.');
        return;
      }

      setTargets(nextTargets);
      setFullStart(unionStart);
      setFullEnd(unionEnd);

      const restoreStart = restoreRef.current.startIso
        ? new Date(restoreRef.current.startIso)
        : null;
      const restoreEnd = restoreRef.current.endIso
        ? new Date(restoreRef.current.endIso)
        : null;
      const nextStart =
        restoreStart && !Number.isNaN(restoreStart.getTime())
          ? clampDate(restoreStart, unionStart, unionEnd)
          : unionStart;
      const nextEnd =
        restoreEnd && !Number.isNaN(restoreEnd.getTime())
          ? clampDate(restoreEnd, unionStart, unionEnd)
          : unionEnd;
      setStartState(
        nextStart.getTime() <= nextEnd.getTime() ? nextStart : nextEnd
      );
      setEndState(
        nextStart.getTime() <= nextEnd.getTime() ? nextEnd : nextStart
      );
      setStatus('ready');
    };

    void discover();
    return () => {
      cancelled = true;
    };
  }, [targetKey, targetsMeta, getAddedExternalLayers, isExternalLayersReady]);

  const applyToTargets = useCallback(
    (
      nextStart: Date,
      nextEnd: Date,
      applyTargets: SharedTnGuardLayerTarget[]
    ) => {
      const rangeStart =
        nextStart.getTime() <= nextEnd.getTime() ? nextStart : nextEnd;
      const rangeEnd =
        nextStart.getTime() <= nextEnd.getTime() ? nextEnd : nextStart;

      for (const target of applyTargets) {
        const featureLayers = collectFeatureLayers(
          getAddedExternalLayers(target.layerId)
        );
        applyExternalLayerTimeWindow({
          layerId: target.layerId,
          featureLayers,
          mode: target.mode,
          dateField: target.dateField,
          start: rangeStart,
          end: rangeEnd,
          setExternalLayerTimeFilter,
          setLayerDefinitionExpressionTimePart,
        });
      }
    },
    [
      getAddedExternalLayers,
      setExternalLayerTimeFilter,
      setLayerDefinitionExpressionTimePart,
    ]
  );

  useEffect(() => {
    if (status !== 'ready' || !start || !end || targets.length === 0) return;
    applyToTargets(start, end, targets);
  }, [status, start, end, targets, applyToTargets]);

  const setStart = useCallback(
    (date: Date) => {
      if (!fullStart || !fullEnd) return;
      const clamped = clampDate(date, fullStart, fullEnd);
      setStartState(clamped);
      setEndState((prevEnd) => {
        if (!prevEnd) return clamped;
        return prevEnd.getTime() < clamped.getTime() ? clamped : prevEnd;
      });
    },
    [fullStart, fullEnd]
  );

  const setEnd = useCallback(
    (date: Date) => {
      if (!fullStart || !fullEnd) return;
      const clamped = clampDate(date, fullStart, fullEnd);
      setEndState(clamped);
      setStartState((prevStart) => {
        if (!prevStart) return clamped;
        return prevStart.getTime() > clamped.getTime() ? clamped : prevStart;
      });
    },
    [fullStart, fullEnd]
  );

  const setRangeFromSlider = useCallback(
    (startValue: number, endValue: number) => {
      if (!fullStart || !fullEnd) return;
      const a = sliderValueToDate(startValue, fullStart, fullEnd);
      const b = sliderValueToDate(endValue, fullStart, fullEnd);
      if (a.getTime() <= b.getTime()) {
        setStartState(a);
        setEndState(b);
      } else {
        setStartState(b);
        setEndState(a);
      }
    },
    [fullStart, fullEnd]
  );

  return useMemo(
    () => ({
      start,
      end,
      setStart,
      setEnd,
      setRangeFromSlider,
      fullStart,
      fullEnd,
      status,
      message,
      targetCount: targets.length,
    }),
    [
      start,
      end,
      setStart,
      setEnd,
      setRangeFromSlider,
      fullStart,
      fullEnd,
      status,
      message,
      targets.length,
    ]
  );
}
